import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { dismissError } from "../../../store/subscriptionSlice";

interface ConnectionStatusBannerProps {
  onRetry?: () => void;
}

const ConnectionStatusBanner: React.FC<ConnectionStatusBannerProps> = ({
  onRetry,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    connectionStatus,
    reconnectAttempts,
    maxReconnectAttempts,
    errorDismissed,
  } = useSelector((state: RootState) => state.subscription);

  // Don't show banner if connected or error dismissed
  if (connectionStatus === "connected" || errorDismissed) {
    return null;
  }

  // Reconnecting state - show yellow banner with spinner
  if (connectionStatus === "reconnecting") {
    return (
      <div className="mx-4 mb-4 rounded-eai-card border border-eai-warning-text/25 bg-eai-brand-peach p-3">
        <div className="flex items-center gap-2">
          <svg
            className="animate-spin h-4 w-4 text-eai-warning-text"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="text-eai-body-sm text-eai-ink">
            Reconnecting... (attempt {reconnectAttempts} of{" "}
            {maxReconnectAttempts})
          </span>
        </div>
      </div>
    );
  }

  // Disconnected state - show red banner with retry option
  const isMaxAttemptsReached = reconnectAttempts >= maxReconnectAttempts;

  return (
    <div className="mx-4 mb-4 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-eai-danger-text"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-eai-body-sm text-eai-danger-text">
            {isMaxAttemptsReached
              ? "Connection lost. Unable to reconnect automatically."
              : "Connection interrupted."}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isMaxAttemptsReached && onRetry && (
            <button
              onClick={onRetry}
              className="text-eai-body-sm font-medium text-eai-danger-text underline transition-colors hover:text-eai-ink"
            >
              Retry
            </button>
          )}
          <button
            onClick={() => dispatch(dismissError())}
            className="text-eai-danger-text-soft transition-colors hover:text-eai-danger-text"
            aria-label="Dismiss"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionStatusBanner;
